import { NativeSyntheticEvent, NativeScrollEvent, FlatList } from 'react-native';
import { SharedValue, runOnJS } from 'react-native-reanimated';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { isWeb } from '@helpers';

interface UseHandleImageScrollParams {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  imageWidth: number;
  scrollProgress: SharedValue<number>;
  flatListRef: RefObject<FlatList<any>>;
}

export const useHandleImageScroll = ({ setActiveIndex, imageWidth, scrollProgress, flatListRef, activeIndex }: UseHandleImageScrollParams) => {
  const lastSelectedIndex = useRef<number>(-1);
  const isScrolling = useRef<boolean>(false);
  const scrollEndTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastProcessedOffset = useRef<number>(-1);

  const updateIndexIfNeeded = (currentIndex: number) => {
    if (currentIndex !== lastSelectedIndex.current && currentIndex >= 0) {
      lastSelectedIndex.current = currentIndex;
      setActiveIndex(currentIndex);
    }
  };

  const handleMainSliderImageScrollEnd = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetX = event.nativeEvent.contentOffset.x;
    // For inverted FlatList on tablet/mobile, index calculation is straightforward
    const newIndex = Math.round(offsetX / imageWidth);

    // Clear any pending scroll updates
    if (scrollEndTimeout.current) {
      clearTimeout(scrollEndTimeout.current);
      scrollEndTimeout.current = null;
    }

    // Mark scrolling as ended
    isScrolling.current = false;

    // Set the final index
    updateIndexIfNeeded(newIndex);

    // Update scroll progress to exact index position to align thumbnails
    if (!isWeb) {
      scrollProgress.value = newIndex;
    }
  };

  const handleMainSliderImageScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    'worklet';
    // Only track continuous scroll on tablet/mobile
    if (!isWeb) {
      const offsetX = event.nativeEvent.contentOffset.x;
      // For inverted FlatList, progress is simply the current offset divided by width
      const progress = offsetX / imageWidth;
      const currentIndex = Math.round(progress);

      scrollProgress.value = progress;

      // Mark that scrolling is active
      runOnJS(() => {
        isScrolling.current = true;

        // Clear existing timeout
        if (scrollEndTimeout.current) {
          clearTimeout(scrollEndTimeout.current);
        }

        // Debounce index updates during continuous scrolling
        scrollEndTimeout.current = setTimeout(() => {
          if (isScrolling.current) {
            updateIndexIfNeeded(currentIndex);
          }
        }, 100); // Wait 100ms before updating during scroll
      })();
    }
  };

  useEffect(() => {
    if (isWeb) return;

    const roundedProgress = Math.round(scrollProgress.value);
    if (roundedProgress !== activeIndex) {
      if (flatListRef.current) {
        try {
          flatListRef.current.scrollToIndex({
            index: activeIndex,
            animated: true,
          });
        } catch (err) {
          console.warn('⚠️ scrollToIndex failed:', err);
        }
      }
    }
  }, [activeIndex, isWeb]);

  return { handleMainSliderImageScrollEnd, handleMainSliderImageScroll };
};
